import React, { useCallback, useEffect, useReducer, useState } from "react"
import {
    obterEstabelecimentos,
    adicionarEstabelecimento,
    desassociarEstabelecimento,
    criarEstabelecimentoAplicativo,
} from "../../../services/estabelecimentosAplicativo"
import type {
    EstabelecimentoCriado,
    NovoEstabelecimentoPayload,
} from "../../../services/estabelecimentosAplicativo"
import useLoadingStore from "../../../stores/LoadingStore"
import { Loading } from "../../RegistradorDePedidos"
import { EstabelecimentosAplicativoInicial } from "../models/estabelecimentosAplicativoModel"
import { EstabelecimentosAplicativoActionsTypes } from "./estabelecimentosAplicativoActions"
import { EstabelecimentosAplicativoContext } from "./EstabelecimentosAplicativoContext"
import { estabelecimentosAplicativoReducer } from "./estabelecimentosAplicativoReducer"

export function EstabelecimentosAplicativoContextProvider({ children }: { children: React.ReactNode }) {
    const [dados, dispatch] = useReducer(
        estabelecimentosAplicativoReducer,
        EstabelecimentosAplicativoInicial
    )
    const [erroCarregamento, setErroCarregamento] = useState<string | null>(null)
    const setLoading = useLoadingStore(s => s.setLoading)

    const carregar = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        try {
            setLoading(true)
            const response = await obterEstabelecimentos(autorizacao)
            if ("retornoErro" in response) {
                setErroCarregamento(response.mensagem || "Erro ao carregar estabelecimentos")
            } else {
                setErroCarregamento(null)
                dispatch({
                    type: EstabelecimentosAplicativoActionsTypes.CARREGAR,
                    payload: {
                        estabelecimentosDoApp: response.estabelecimentosDoApp ?? [],
                        todosEstabelecimentos: response.todosEstabelecimentos ?? [],
                        podeEditar: response.podeEditar !== false,
                    },
                })
            }
        } catch {
            setErroCarregamento("Erro ao carregar estabelecimentos")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        carregar()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const adicionar = useCallback(
        async (idEstabelecimento: number) => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao) return
            setLoading(true)
            try {
                const response = await adicionarEstabelecimento(autorizacao, idEstabelecimento)
                if ("retornoErro" in response) {
                    throw new Error(response.mensagem)
                }
                dispatch({
                    type: EstabelecimentosAplicativoActionsTypes.ADICIONAR_ESTAB,
                    payload: response,
                })
            } finally {
                setLoading(false)
            }
        },
        [setLoading]
    )

    const desassociar = useCallback(
        async (idEstabelecimento: number) => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao) return
            setLoading(true)
            try {
                const response = await desassociarEstabelecimento(autorizacao, idEstabelecimento)
                if (response && "retornoErro" in response) {
                    throw new Error(response.mensagem)
                }
                dispatch({
                    type: EstabelecimentosAplicativoActionsTypes.REMOVER_ESTAB,
                    payload: idEstabelecimento,
                })
            } finally {
                setLoading(false)
            }
        },
        [setLoading]
    )

    const criarNovo = useCallback(
        async (payload: NovoEstabelecimentoPayload): Promise<EstabelecimentoCriado> => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao) throw new Error("Sessão expirada. Faça login novamente.")
            setLoading(true)
            try {
                const response = await criarEstabelecimentoAplicativo(autorizacao, payload)
                if ("retornoErro" in response) {
                    throw new Error(response.mensagem)
                }
                dispatch({
                    type: EstabelecimentosAplicativoActionsTypes.ADICIONAR_ESTAB,
                    payload: { id: response.id, nome: response.nome, visivel: response.visivel },
                })
                return response
            } finally {
                setLoading(false)
            }
        },
        [setLoading]
    )

    return (
        <EstabelecimentosAplicativoContext.Provider
            value={{ dados, dispatch, adicionar, desassociar, criarNovo, erroCarregamento }}
        >
            <Loading />
            {children}
        </EstabelecimentosAplicativoContext.Provider>
    )
}
